import React, { useEffect, useRef, useState } from 'react'
import mermaid from 'mermaid'

// ✅ Initieer Mermaid één keer
mermaid.initialize({
    startOnLoad: false,
    theme: 'default', // i.p.v. 'neutral'
    securityLevel: 'loose',
})

export default function MermaidRenderer({ chart }) {
    const ref = useRef(null)
    const [isModalOpen, setModalOpen] = useState(false)
    const [svgContent, setSvgContent] = useState('')

    useEffect(() => {
        if (!chart || typeof chart !== 'string') {
            if (ref.current)
                ref.current.innerHTML =
                    '<em style="color:gray;">(Geen diagramdata)</em>'
            return
        }

        // 🧩 Verwijder eventuele ```mermaid``` fences
        let cleanChart = chart.trim()
        if (cleanChart.startsWith('```')) {
            cleanChart = cleanChart
                .replace(/^```(mermaid)?/, '')
                .replace(/```$/, '')
                .trim()
        }

        // 🧹 Unescape JSON escapes and HTML entities
        cleanChart = cleanChart
            // Convert JSON escaped newlines/tabs
            .replace(/\\n/g, '\n')
            .replace(/\\t/g, '\t')
            // Fix escaped forward slashes
            .replace(/\\\//g, '/')
            .replace(/\\\\/g, '\\')
            // Decode HTML escapes: \u003E → >
            .replace(/\\u003E/g, '>')
            // Decode accented letters (e.g. \u00e9 → é)
            .replace(/\\u([0-9a-fA-F]{4})/g, (_, code) =>
                String.fromCharCode(parseInt(code, 16))
            )

        // 🧩 Decode HTML entities (zoals \u003E → >)
        const decodeHtml = (html) => {
            const el = document.createElement('textarea')
            el.innerHTML = html
            return el.value
        }
        const decoded = decodeHtml(cleanChart)

        // 🧱 Fix Gantt labels met dubbele punt (Mermaid breekt daarop)
        cleanChart = cleanChart.replace(
            /^([A-Za-z0-9 .()/_-]+:[^:]+):(\d{4}-\d{2}-\d{2})/gm,
            (_, label, date) => `"${label.trim()}" :${date}`
        )

        const id = `graph-${Math.random().toString(36).substring(2, 8)}`

        const renderChart = async () => {
            try {
                let rawSvg

                if (typeof mermaid.renderAsync === 'function') {
                    const result = await mermaid.renderAsync(id, decoded)
                    rawSvg = result?.svg || result
                } else if (typeof mermaid.render === 'function') {
                    const possibleResult = mermaid.render(id, decoded)
                    if (
                        possibleResult &&
                        typeof possibleResult.then === 'function'
                    ) {
                        const result = await possibleResult
                        rawSvg = result?.svg || result
                    } else {
                        rawSvg = possibleResult?.svg || possibleResult
                    }
                }

                if (typeof rawSvg !== 'string') {
                    throw new Error('Mermaid output was not a string')
                }

                // 🧼 Maak SVG responsief
                const responsiveSvg = rawSvg
                    .replace(/width="[^"]*"/, 'width="100%"')
                    .replace(/height="[^"]*"/, 'height="auto"')
                    .replace(
                        /<svg /,
                        '<svg preserveAspectRatio="xMidYMid meet" style="max-width:100%;height:auto;" '
                    )

                setSvgContent(responsiveSvg)
                if (ref.current) ref.current.innerHTML = responsiveSvg
            } catch (err) {
                console.error('Mermaid render failed:', err)
                if (ref.current)
                    ref.current.innerHTML = `<pre style="color:red;">❌ Ongeldig Mermaid-diagram:\n${decoded}</pre>`
            }
        }

        renderChart()
    }, [chart])

    const handleDownload = () => {
        if (!svgContent) return
        const blob = new Blob([svgContent], {
            type: 'image/svg+xml;charset=utf-8',
        })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'diagram.svg'
        a.click()
        URL.revokeObjectURL(url)
    }

    return (
        <>
            {/* 🧩 Inline preview */}
            <div
                ref={ref}
                className="mermaid"
                style={{
                    maxWidth: '100%',
                    maxHeight: '180px',
                    overflow: 'hidden',
                    cursor: 'zoom-in',
                    borderRadius: '8px',
                    position: 'relative',
                    border: '1px solid #ddd',
                    background: '#fff',
                }}
                onClick={() => setModalOpen(true)}
                title="Klik om te vergroten"
            >
                <div
                    style={{
                        position: 'absolute',
                        bottom: '6px',
                        right: '6px',
                        background: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        fontSize: '12px',
                        padding: '4px 6px',
                        borderRadius: '4px',
                    }}
                >
                    🔍
                </div>
            </div>

            {/* 🪟 Fullscreen modal viewer */}
            {isModalOpen && (
                <div
                    onClick={() => setModalOpen(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                    }}
                >
                    <div
                        style={{
                            position: 'relative',
                            background: 'white',
                            borderRadius: '12px',
                            padding: '20px',
                            width: '90vw',
                            height: '90vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'auto',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            dangerouslySetInnerHTML={{ __html: svgContent }}
                        />

                        {/* 🔘 Controls */}
                        <div
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '12px',
                                display: 'flex',
                                gap: '10px',
                            }}
                        >
                            <button
                                onClick={handleDownload}
                                style={{
                                    background: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '6px 10px',
                                    cursor: 'pointer',
                                }}
                            >
                                ⬇️ Download
                            </button>
                            <button
                                onClick={() => setModalOpen(false)}
                                style={{
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '6px 10px',
                                    cursor: 'pointer',
                                }}
                            >
                                ✖ Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
